import { toUtf8 } from "@aws-sdk/util-utf8-browser"
import { iot, mqtt5 } from "aws-iot-device-sdk-v2/dist/browser"

import { useAppDispatch } from "../app/hooks"
import {
  batchUpdateCourierPositions,
} from "../app/slices/dashboard.slice"
import {
  addSubscribedTopic,
  setConnectionStatus,
  setError,
  setLastMessage,
  setSubscribedTopics,
} from "../app/slices/wsSlice"
import { debugLog } from "./debugUtils"

class WebSocketService {
  private dispatch: ReturnType<typeof useAppDispatch> | null = null
  private api: any = null
  private client: mqtt5.Mqtt5Client | null = null
  private reconnectTimer: NodeJS.Timeout | null = null
  private isConnecting = false
  private retryCount = 0
  private MAX_RETRIES = 5
  private topicCallbacks = new Map<
    string,
    Set<(topic: string, message: any) => void>
  >()
  private subscribedTopics = new Set<string>()
  private pendingSubscriptions = new Map<
    string,
    Set<(topic: string, message: any) => void>
  >()
  private refetchConfig: (() => Promise<any>) | null = null
  private orderInvalidationTimer: ReturnType<typeof setTimeout> | null = null
  private pendingPositionUpdates: Map<number, any> = new Map()
  private positionFlushScheduled = false
  private currentOrdersDateArg: string | null = null

  setDispatch(dispatch: ReturnType<typeof useAppDispatch>) {
    this.dispatch = dispatch
  }

  setApi(api: any) {
    this.api = api
  }

  setRefetchConfig(refetchConfig: () => Promise<any>) {
    this.refetchConfig = refetchConfig
  }

  setCurrentOrdersDateArg(date: string) {
    this.currentOrdersDateArg = date
  }

  async connect(wsConfig: any) {
    if (this.isConnecting || this.client) {
      debugLog("[ws] connect() skipped — already connecting or connected")
      return
    }

    if (this.retryCount > this.MAX_RETRIES) {
      debugLog("Max retries exceeded. Giving up.")
      this.dispatch?.(setError("Max retries exceeded. Giving up."))
      return
    }

    this.isConnecting = true
    this.dispatch?.(setConnectionStatus("connecting"))
    debugLog("[ws] Connecting to IoT", {
      endpoint: wsConfig.iotEndpoint,
      authorizer: wsConfig.authorizerName,
      topicsFromBackend: wsConfig.topics,
      providerCodes: wsConfig.providerCodes,
      haulerUuid: wsConfig.haulerUuid,
    })

    try {
      const customAuthConfig: iot.MqttConnectCustomAuthConfig = {
        authorizerName: wsConfig.authorizerName,
        tokenKeyName: wsConfig.tokenKeyName,
        tokenValue: wsConfig.token,
        tokenSignature: wsConfig.tokenSignature,
      }

      const builder =
        iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithCustomAuth(
          wsConfig.iotEndpoint,
          customAuthConfig,
        )

      this.client = new mqtt5.Mqtt5Client(builder.build())

      this.client.on("messageReceived", (eventData) =>
        this.onMessageReceived(eventData),
      )

      this.client.on("connectionSuccess", (eventData) => {
        debugLog("[ws] connectionSuccess", eventData)
        this.isConnecting = false
        this.retryCount = 0
        this.dispatch?.(setConnectionStatus("connected"))

        // Use backend-provided topics; fall back to client-side construction
        let topics: string[] = wsConfig.topics
        if (!topics?.length) {
          debugLog(
            "[ws] No topics from backend, constructing from providerCodes/haulerUuid",
          )
          const providerCodes: string[] = wsConfig.providerCodes || []
          topics = providerCodes.flatMap((code: string) => [
            `provider/order/${code}`,
            `provider/notification/${code}`,
          ])
          if (wsConfig.haulerUuid) {
            topics.push(`h_m/${wsConfig.haulerUuid}`)
          }
        }

        debugLog("[ws] Subscribing to topics:", topics)
        if (topics.length > 0) {
          this.subscribeToTopics(topics)
        } else {
          debugLog("[ws] WARNING: No topics to subscribe to!")
        }
        this.processPendingSubscriptions()
      })

      this.client.on("connectionFailure", (error) => {
        debugLog("[ws] ✗ connectionFailure", error)
        this.client = null
        this.isConnecting = false
        this.retryCount++
        this.dispatch?.(setError("Connection failed"))
        this.scheduleReconnect(wsConfig)
      })

      this.client.on("disconnection", (eventData) => {
        debugLog("[ws] Disconnected from AWS IoT", eventData)
        this.client = null
        this.dispatch?.(setConnectionStatus("disconnected"))
        this.scheduleReconnect(wsConfig)
      })

      this.client.start()
    } catch (error) {
      console.error("Failed to connect to AWS IoT:", error)
      this.isConnecting = false
      this.retryCount++
      this.dispatch?.(
        setError(error instanceof Error ? error.message : "Connection failed"),
      )
      this.scheduleReconnect(wsConfig)
    }
  }

  private onMessageReceived(eventData: any) {
    debugLog("[ws] Message received", eventData?.message?.topicName)
    const { message } = eventData
    const { topicName, payload } = message

    if (!payload) {
      debugLog("payload is null")
      return
    }

    const messageStr = toUtf8(payload as Uint8Array)

    try {
      const data = JSON.parse(messageStr)
      this.handleMessage(topicName, data)
    } catch (err) {
      debugLog("Error parsing message", err)
    }
  }

  private static readonly MAX_TOPICS_PER_SUBSCRIBE = 8

  private async subscribeToTopics(topics: string[]) {
    if (!this.client) {
      debugLog("[ws] subscribeToTopics: no client, skipping")
      return
    }

    debugLog(`[ws] Subscribing to ${topics.length} topics...`)

    try {
      // AWS IoT limits SUBSCRIBE to 8 topic filters per call — batch them
      for (
        let i = 0;
        i < topics.length;
        i += WebSocketService.MAX_TOPICS_PER_SUBSCRIBE
      ) {
        const batch = topics.slice(
          i,
          i + WebSocketService.MAX_TOPICS_PER_SUBSCRIBE,
        )
        const subscriptions = batch.map((topic) => ({
          topicFilter: topic,
          qos: mqtt5.QoS.AtLeastOnce,
        }))

        const subResp = await this.client.subscribe({
          subscriptions,
        })

        for (let j = 0; j < subscriptions.length; j++) {
          const reasonCode = subResp?.reasonCodes[j]
          const sub = subscriptions[j]
          if (["0", "1", "2"].includes(reasonCode + "")) {
            debugLog(`[ws] ✓ subscribed: [${sub.topicFilter}]`)
            this.dispatch?.(addSubscribedTopic(sub.topicFilter))
          } else {
            debugLog(
              `[ws] ✗ FAILED subscribe: [${sub.topicFilter}] reason=${reasonCode}`,
            )
            console.error(
              `Error subscribing : [${sub.topicFilter}]`,
              reasonCode,
            )
          }
        }
      }

      this.dispatch?.(setSubscribedTopics(topics))
    } catch (error) {
      debugLog("[ws] ✗ Subscribe error:", error)
      console.error("Failed to subscribe to topics:", error)
      this.dispatch?.(setError("Failed to subscribe to topics"))
    }
  }

  private async processPendingSubscriptions() {
    if (this.pendingSubscriptions.size === 0) return

    debugLog(
      "Processing pending subscriptions:",
      Array.from(this.pendingSubscriptions.keys()),
    )

    for (const [topic, callbacks] of this.pendingSubscriptions) {
      let activeCallbacks = this.topicCallbacks.get(topic)
      if (!activeCallbacks) {
        activeCallbacks = new Set()
        this.topicCallbacks.set(topic, activeCallbacks)
      }
      callbacks.forEach((callback) => activeCallbacks!.add(callback))

      if (!this.subscribedTopics.has(topic)) {
        try {
          const subResp = await this.client!.subscribe({
            subscriptions: [
              {
                topicFilter: topic,
                qos: mqtt5.QoS.AtLeastOnce,
              },
            ],
          })
          const reasonCode = subResp?.reasonCodes[0]
          if (["0", "1", "2"].includes(reasonCode + "")) {
            debugLog(`Successfully subscribed to pending topic: [${topic}]`)
            this.dispatch?.(addSubscribedTopic(topic))
            this.subscribedTopics.add(topic)
          } else {
            console.error(
              `Error subscribing to pending topic: [${topic}]`,
              reasonCode,
            )
          }
        } catch (error) {
          console.error("Failed to subscribe to pending topic:", topic, error)
        }
      }
    }

    this.pendingSubscriptions.clear()
  }

  async subscribe(
    topics: string[],
    onMessage?: (topic: string, message: any) => void,
  ) {
    if (this.getConnectionStatus() !== "connected" || !this.client) {
      debugLog(
        "WebSocket client not connected. Buffering subscription for topics:",
        topics,
      )
      for (const topic of topics) {
        let callbacks = this.pendingSubscriptions.get(topic)
        if (!callbacks) {
          callbacks = new Set()
          this.pendingSubscriptions.set(topic, callbacks)
        }
        if (onMessage) {
          callbacks.add(onMessage)
        }
      }
      return
    }
    debugLog("Subscribing to topics:", topics)
    try {
      const topicsToSubscribe: string[] = []

      for (const topic of topics) {
        let callbacks = this.topicCallbacks.get(topic)
        if (!callbacks) {
          callbacks = new Set()
          this.topicCallbacks.set(topic, callbacks)
        }

        if (onMessage) {
          callbacks.add(onMessage)
        }

        if (!this.subscribedTopics.has(topic)) {
          topicsToSubscribe.push(topic)
          this.subscribedTopics.add(topic)
        }
      }

      if (topicsToSubscribe.length > 0) {
        const subscriptions = topicsToSubscribe.map((topic) => ({
          topicFilter: topic,
          qos: mqtt5.QoS.AtLeastOnce,
        }))

        const subResp = await this.client.subscribe({
          subscriptions,
        })

        for (let i = 0; i < subscriptions.length; i++) {
          const reasonCode = subResp?.reasonCodes[i]
          const sub = subscriptions[i]
          if (["0", "1", "2"].includes(reasonCode + "")) {
            debugLog(`Successfully subscribed to: [${sub.topicFilter}]`)
            this.dispatch?.(addSubscribedTopic(sub.topicFilter))
          } else {
            console.error(
              `Error subscribing : [${sub.topicFilter}]`,
              reasonCode,
            )
            this.subscribedTopics.delete(sub.topicFilter)
          }
        }
      }
    } catch (error) {
      console.error("Failed to subscribe:", error)
    }
  }

  async unsubscribe(
    topics: string[],
    onMessage?: (topic: string, message: any) => void,
  ) {
    if (this.getConnectionStatus() !== "connected" || !this.client) {
      console.warn(
        "WebSocket client not connected. Cannot unsubscribe from topics.",
      )
      return
    }

    try {
      const topicsToUnsubscribe: string[] = []

      for (const topic of topics) {
        const callbacks = this.topicCallbacks.get(topic)
        if (callbacks && onMessage) {
          callbacks.delete(onMessage)
          if (callbacks.size === 0) {
            this.topicCallbacks.delete(topic)
            if (this.subscribedTopics.has(topic)) {
              topicsToUnsubscribe.push(topic)
              this.subscribedTopics.delete(topic)
            }
          }
        }
      }

      if (topicsToUnsubscribe.length > 0) {
        await this.client.unsubscribe({
          topicFilters: topicsToUnsubscribe,
        })
        debugLog("Successfully unsubscribed from:", topicsToUnsubscribe)
      }
    } catch (error) {
      console.error("Failed to unsubscribe:", error)
    }
  }

  private handleMessage(topic: string, message: any) {
    try {
      debugLog("[ws] ← Received:", topic, message?.type ?? message)

      this.dispatch?.(setLastMessage({ topic, message, timestamp: Date.now() }))

      // Check for topic-specific callbacks
      const topicCallbacks = this.topicCallbacks.get(topic)
      if (topicCallbacks) {
        topicCallbacks.forEach((callback) => {
          try {
            callback(topic, message)
          } catch (error) {
            console.error("Error in topic callback:", error)
          }
        })
      }

      // Handle entity updates — invalidate RTK Query Orders cache or update positions
      this.handleEntityUpdate(topic, message)
    } catch (error) {
      console.error("Failed to parse message:", error)
    }
  }

  private handleEntityUpdate(topic: string, message?: any) {
    if (!this.api || !this.dispatch) {
      debugLog(
        "[ws] handleEntityUpdate: api or dispatch not set, skipping invalidation",
      )
      return
    }

    // Only handle Orders cache on order/notification topics
    // h_m/ topics are driver positions — handled via direct Redux dispatch, not cache invalidation
    if (
      topic.startsWith("provider/order/") ||
      topic.startsWith("provider/notification/")
    ) {
      // Try optimistic patch first; fall back to debounced invalidation
      if (!this.tryOptimisticOrderPatch(message)) {
        debugLog("[ws] → Invalidating Orders cache due to MQTT on:", topic)
        this.invalidateOrdersCache()
      }
    } else if (topic.startsWith("h_m/")) {
      // Hauler broadcast — order updates from registerRepoHooks
      if (!this.tryOptimisticOrderPatch(message)) {
        debugLog("[ws] → Invalidating Orders cache due to MQTT on:", topic)
        this.invalidateOrdersCache()
      }
    } else if (topic.startsWith("mrt/app/delivery-be/")) {
      // CourierPositionUpdate — update driver position in state
      if (message?.type === "CourierPositionUpdate" && message?.data?.latest) {
        const { haulerEmployeeId, haulerEmployeeUuid, latest, recent } =
          message.data
        debugLog(
          "[ws] → Courier position update for employee:",
          haulerEmployeeId,
        )
        const recentPositions = (recent ?? []).map((p: any) => ({
          latitude: Array.isArray(p) ? p[0] : p.latitude,
          longitude: Array.isArray(p) ? p[1] : p.longitude,
          timestamp: Array.isArray(p) ? "" : p.timestamp,
        }))
        this.pendingPositionUpdates.set(haulerEmployeeId, {
          haulerEmployeeId,
          haulerEmployeeUuid,
          latitude: latest.latitude,
          longitude: latest.longitude,
          heading: latest.heading ?? 0,
          speed: latest.speed ?? 0,
          accuracy: latest.accuracy ?? 10,
          altitude: latest.altitude ?? 0,
          odometer: latest.odometer ?? 0,
          batteryLevel: latest["battery.level"] ?? 0,
          timestamp: latest.timestamp,
          recent: recentPositions,
        })
        this.schedulePositionFlush()
      }
    } else {
      debugLog("[ws] Message on unhandled topic (no invalidation):", topic)
    }
  }

  private tryOptimisticOrderPatch(message: any): boolean {
    if (!this.api || !this.dispatch || !this.currentOrdersDateArg) return false

    // Only handle OrderMessage with UPDATE action and a full order payload
    if (message?.type !== "OrderMessage" || message?.data?.action !== "UPDATE") {
      return false
    }
    const updatedOrder = message.data.order
    if (!updatedOrder?.id) return false

    debugLog("[ws] → Optimistic patch for order:", updatedOrder.id)
    let patched = false
    this.dispatch(
      this.api.util.updateQueryData(
        "getOrdersByDate",
        this.currentOrdersDateArg,
        (draft: any[]) => {
          const index = draft.findIndex((o: any) => o.id === updatedOrder.id)
          if (index !== -1) {
            Object.assign(draft[index], updatedOrder)
            patched = true
          }
        },
      ),
    )
    // If order wasn't in cache (new order or changed date), fall back to invalidation
    if (!patched) {
      debugLog("[ws] → Order not in cache, falling back to invalidation")
    }
    return patched
  }

  private invalidateOrdersCache() {
    if (this.orderInvalidationTimer) {
      clearTimeout(this.orderInvalidationTimer)
    }
    this.orderInvalidationTimer = setTimeout(() => {
      this.orderInvalidationTimer = null
      if (this.api && this.dispatch) {
        this.dispatch(
          this.api.util.invalidateTags([{ type: "Orders", id: "LIST" }]),
        )
      }
    }, 1000)
  }

  private schedulePositionFlush() {
    if (this.positionFlushScheduled) return
    this.positionFlushScheduled = true
    requestAnimationFrame(() => {
      this.positionFlushScheduled = false
      if (!this.dispatch || this.pendingPositionUpdates.size === 0) return
      const updates = Array.from(this.pendingPositionUpdates.values())
      this.pendingPositionUpdates.clear()
      this.dispatch(batchUpdateCourierPositions(updates))
    })
  }

  private async scheduleReconnect(wsConfig: any) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.reconnectTimer = setTimeout(async () => {
      debugLog("Attempting to reconnect...")
      try {
        const freshConfig = this.refetchConfig
          ? await this.refetchConfig()
          : wsConfig
        this.connect(freshConfig)
      } catch (error) {
        console.error("Failed to refetch config for reconnection:", error)
        this.connect(wsConfig)
      }
    }, 5000)
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.client) {
      this.client.stop()
      this.client = null
    }

    this.isConnecting = false
    this.retryCount = 0
    this.dispatch?.(setConnectionStatus("disconnected"))
  }

  isConnected(): boolean {
    return this.client !== null
  }

  getConnectionStatus(): "disconnected" | "connecting" | "connected" | "error" {
    if (this.isConnecting) return "connecting"
    if (this.client) return "connected"
    return "disconnected"
  }
}

// Export singleton instance
export const wsService = new WebSocketService()
